import React from "react";
import { Button, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import history from "@history";

import { closeQuickPanel } from "app/main/shared-components/quick-panel/store";
import { CouponService } from "app/services";
import { errorMsg, successMsg } from "app/utils/MessageUtils";
import { fetchPromotionalCoupons } from "./store/promotional-coupons.slice";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

type FormValues = {
  active: "true" | "false";
  code: string;
  discountValue: string;          
  description: string;
  validFrom: string;               
  validUntil: string;             
  maxUses: string;                 
  minPurchaseAmount: string;       
  maxDiscountAmount: string;       
  usageLimitPerCustomer: string;  
};


const today = new Date();
const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate()+1)}`;

const defaultFrom = toYMD(today);
const plus30 = new Date(today.getTime());
plus30.setDate(plus30.getDate() + 30);
const defaultUntil = toYMD(plus30);

const initialValues: FormValues = {
  active: "true",
  code: "",
  discountValue: "",
  description: "",
  validFrom: defaultFrom,
  validUntil: defaultUntil,
  maxUses: "1",
  minPurchaseAmount: "",
  maxDiscountAmount: "",
  usageLimitPerCustomer: "1",
};

const CreateCouponForm: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ts = (key: string, fallback: string) => String(t(key, fallback));

  const onCancel = () => {
    dispatch(closeQuickPanel(null));
    history.push("/coupons/promotional");
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography variant="h6" style={{ marginBottom: 16 }}>
        {ts("app.main.coupons.create.title", "New promotional coupon")}
      </Typography>

      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: any = {};

          // code – required and length 6–50
          const trimmedCode = values.code?.trim() ?? "";
          if (!trimmedCode) {
            errors.code = ts("Required", "Required");
          } else if (trimmedCode.length < 6 || trimmedCode.length > 50) {
            errors.code = ts(
              "Code must be between 6 and 50 characters",
              "Code must be between 6 and 50 characters"
            );
          }

          // discount value – required numeric 1–100
          if (!values.discountValue?.trim()) {
            errors.discountValue = ts("Required", "Required");
          } else if (isNaN(Number(values.discountValue))) {
            errors.discountValue = ts("Invalid number", "Invalid number");
          } else if (
            Number(values.discountValue) <= 0 ||
            Number(values.discountValue) > 100
          ) {
            errors.discountValue = ts(
              "Percent must be between 1 and 100",
              "Percent must be between 1 and 100"
            );
          }

          // validFrom / validUntil – required and From <= Until
          if (!values.validFrom) {
            errors.validFrom = ts("Required", "Required");
          }
          if (!values.validUntil) {
            errors.validUntil = ts("Required", "Required");
          }
          if (values.validFrom && values.validUntil) {
            if (values.validFrom > values.validUntil) {
              errors.validUntil = ts(
                "Valid until must be after valid from",
                "Valid until must be after valid from"
              );
            }
          }

          // maxUses – required, >= 1
          const maxUsesRaw = values.maxUses;
          const maxUsesStr =
            maxUsesRaw === undefined || maxUsesRaw === null
              ? ""
              : String(maxUsesRaw);
          const maxUsesNumber = Number(maxUsesStr);

          if (!maxUsesStr.trim()) {
            errors.maxUses = ts("Required", "Required");
          } else if (Number.isNaN(maxUsesNumber)) {
            errors.maxUses = ts("Invalid number", "Invalid number");
          } else if (maxUsesNumber < 1) {
            errors.maxUses = ts(
              "Max uses must be at least 1",
              "Max uses must be at least 1"
            );
          }

          // minPurchaseAmount – optional, >= 0
          const mpaStr = values.minPurchaseAmount
            ? String(values.minPurchaseAmount).trim()
            : "";
          if (mpaStr) {
            const mpaNum = Number(mpaStr);
            if (Number.isNaN(mpaNum)) {
              errors.minPurchaseAmount = ts("Invalid number", "Invalid number");
            } else if (mpaNum < 0) {
              errors.minPurchaseAmount = ts(
                "Must be at least 0",
                "Must be at least 0"
              );
            }
          }

          // maxDiscountAmount – optional, >= 0
          const mdaStr = values.maxDiscountAmount
            ? String(values.maxDiscountAmount).trim()
            : "";
          if (mdaStr) {
            const mdaNum = Number(mdaStr);
            if (Number.isNaN(mdaNum)) {
              errors.maxDiscountAmount = ts("Invalid number", "Invalid number");
            } else if (mdaNum < 0) {
              errors.maxDiscountAmount = ts(
                "Must be at least 0",
                "Must be at least 0"
              );
            }
          }

          // usageLimitPerCustomer – optional, >= 1 and <= maxUses
          const ulpcStr = values.usageLimitPerCustomer
            ? String(values.usageLimitPerCustomer).trim()
            : "";
          if (ulpcStr) {
            const ulpcNum = Number(ulpcStr);
            if (Number.isNaN(ulpcNum)) {
              errors.usageLimitPerCustomer = ts(
                "Invalid number",
                "Invalid number"
              );
            } else if (ulpcNum < 1) {
              errors.usageLimitPerCustomer = ts(
                "Must be at least 1",
                "Must be at least 1"
              );
            } else if (!Number.isNaN(maxUsesNumber) && ulpcNum > maxUsesNumber) {
              errors.usageLimitPerCustomer = ts(
                "Cannot be greater than max uses",
                "Cannot be greater than max uses"
              );
            }
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const discountNumber = Number(values.discountValue);
          const maxUsesNumber = Number(values.maxUses) || 1;

          const mpaStr = values.minPurchaseAmount
            ? String(values.minPurchaseAmount).trim()
            : "";
          const mdaStr = values.maxDiscountAmount
            ? String(values.maxDiscountAmount).trim()
            : "";
          const ulpcStr = values.usageLimitPerCustomer
            ? String(values.usageLimitPerCustomer).trim()
            : "";

          const minPurchaseAmountNumber = mpaStr ? Number(mpaStr) : 0;
          const maxDiscountAmountNumber = mdaStr ? Number(mdaStr) : 0;
          const usageLimitPerCustomerNumber = ulpcStr ? Number(ulpcStr) : 0;

          const validFromIso = `${values.validFrom}T00:00:00Z`;
          const validUntilIso = `${values.validUntil}T23:59:59Z`;

          const body = {
            code: values.code.trim(),
            description: values.description?.trim() || "",
            discountPercentage: discountNumber,
            isActive: values.active === "true",
            validFrom: validFromIso,
            validUntil: validUntilIso,
            maxUses: maxUsesNumber,
            minPurchaseAmount: minPurchaseAmountNumber,
            maxDiscountAmount: maxDiscountAmountNumber,
            usageLimitPerCustomer: usageLimitPerCustomerNumber,
          };

          try {
            await CouponService.createCoupon(body);

            dispatch(
              successMsg(
                ts(
                  "app.main.coupons.create.success",
                  "Coupon created successfully."
                )
              )
            );

            (dispatch as any)(fetchPromotionalCoupons({ page: 0, size: 50 }));

            resetForm();
            dispatch(closeQuickPanel(null));
            history.push("/coupons/promotional");
          } catch (err) {
            dispatch(
              errorMsg(
                err,
                ts(
                  "app.main.coupons.create.error",
                  "Failed to create coupon. Please check the form and try again."
                )
              )
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, values, errors, touched, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  {ts(
                    "app.main.coupons.create.note",
                    "Coupon code is valid across all languages."
                  )}
                </Typography>
              </Grid>

              {/* Code + Active */}
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="code"
                  label={ts("app.main.coupons.create.code", "Coupon code")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={Boolean(touched.code && errors.code)}
                  helperText={
                    touched.code && errors.code ? String(errors.code) : " "
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue("code", e.target.value.toUpperCase())
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  select
                  name="active"
                  label={ts("app.main.coupons.create.active", "Active")}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="true">{ts("Yes", "Yes")}</MenuItem>
                  <MenuItem value="false">{ts("No", "No")}</MenuItem>
                </Field>
              </Grid>

              {/* Discount percentage */}
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="discountValue"
                  label={ts(
                    "app.main.coupons.create.discountValue",
                    "Discount value in percentage"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={Boolean(touched.discountValue && errors.discountValue)}
                  helperText={
                    touched.discountValue && errors.discountValue
                      ? String(errors.discountValue)
                      : ts(
                          "app.main.coupons.create.percentHint",
                          "Example: 10%"
                        )
                  }
                />
              </Grid>

              {/* Valid from / until */}
              <Grid item xs={12} sm={6}>
               <MuiPickersUtilsProvider utils={DateFnsUtils} >
                <DatePicker
                  label={ts("app.main.coupons.create.validFrom", "Valid from")}
                  format="dd-MM-yyy"
                  value={values.validFrom ? new Date(values.validFrom) : null}
                  onChange={(date: Date | null) =>
                    setFieldValue(
                      "validFrom",
                      date ? date.toISOString().slice(0, 10) : ""
                    )
                  }
                  inputVariant="outlined"
                  size="small"
                  autoOk
                  disableToolbar
                  variant="inline"
                  minDate={new Date(defaultFrom)}
                  fullWidth
                  error={Boolean(touched.validFrom && errors.validFrom)}
                  helperText={
                    touched.validFrom && errors.validFrom
                      ? String(errors.validFrom)
                      : " "
                  }
                />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    label={ts("app.main.coupons.create.validUntil", "Valid until")}
                    format="dd-MM-yyyy"
                    value={values.validUntil ? new Date(values.validUntil) : null}
                    onChange={(date: Date | null) =>
                      setFieldValue(
                        "validUntil",
                        date ? date.toISOString().slice(0, 10) : ""
                      )
                    }
                    inputVariant="outlined"
                    size="small"
                    autoOk
                    disableToolbar
                    variant="inline"
                    minDate={
                      values.validFrom
                        ? new Date(values.validFrom)
                        : new Date(defaultFrom)
                    }
                    fullWidth
                    error={Boolean(touched.validUntil && errors.validUntil)}
                    helperText={
                      touched.validUntil && errors.validUntil
                        ? String(errors.validUntil)
                        : " "
                    }
                  />
                </MuiPickersUtilsProvider> 
              </Grid>


              {/* Max uses */}
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  type="number"
                  name="maxUses"
                  label={ts(
                    "app.main.coupons.create.maxUses",
                    "Max uses"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 1 }}
                  error={Boolean(touched.maxUses && errors.maxUses)}
                  helperText={
                    touched.maxUses && errors.maxUses
                      ? String(errors.maxUses)
                      : ts(
                          "app.main.coupons.create.maxUsesHint",
                          "Minimum 1; total number of times this code can be used."
                        )
                  }
                />
              </Grid>

              {/* Three numeric fields side by side */}
              {/* <Grid item xs={12} sm={4}>
                <Field
                  as={TextField}
                  type="number"
                  name="minPurchaseAmount"
                  label={ts(
                    "app.main.coupons.create.minPurchaseAmount",
                    "Minimum purchase amount"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0 }}
                  error={Boolean(
                    touched.minPurchaseAmount && errors.minPurchaseAmount
                  )}
                  helperText={
                    touched.minPurchaseAmount && errors.minPurchaseAmount
                      ? String(errors.minPurchaseAmount)
                      : ts(
                          "app.main.coupons.create.minPurchaseAmountHint",
                          "Optional; minimum order total required to apply this coupon."
                        )
                  }
                />
              </Grid> */}

              {/* <Grid item xs={12} sm={4}>
                <Field
                  as={TextField}
                  type="number"
                  name="maxDiscountAmount"
                  label={ts(
                    "app.main.coupons.create.maxDiscountAmount",
                    "Maximum discount amount"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0 }}
                  error={Boolean(
                    touched.maxDiscountAmount && errors.maxDiscountAmount
                  )}
                  helperText={
                    touched.maxDiscountAmount && errors.maxDiscountAmount
                      ? String(errors.maxDiscountAmount)
                      : ts(
                          "app.main.coupons.create.maxDiscountAmountHint",
                          "Optional; maximum discount value that can be applied."
                        )
                  }
                />
              </Grid> */}

              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  type="number"
                  name="usageLimitPerCustomer"
                  label={ts(
                    "app.main.coupons.create.usageLimitPerCustomer",
                    "Max uses per customer"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={values.usageLimitPerCustomer ?? 1}
                  inputProps={{ min: 1 }}
                  error={Boolean(
                    touched.usageLimitPerCustomer &&
                      errors.usageLimitPerCustomer
                  )}
                  helperText={
                    touched.usageLimitPerCustomer &&
                    errors.usageLimitPerCustomer
                      ? String(errors.usageLimitPerCustomer)
                      : ts(
                          "app.main.coupons.create.usageLimitPerCustomerHint",
                          "Minimum 1;if set, how many times each customer can use this coupon.     "
                        )
                  }
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="description"
                  label={ts(
                    "app.main.coupons.create.description",
                    "Description"
                  )}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Actions */}
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                <Button onClick={onCancel} disabled={isSubmitting}>
                  {ts("Cancel", "Cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {ts("Save", "Save")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
   </div>
  );
};

export default CreateCouponForm;
